// Hàm thuần cho "Đo chuyển đổi" (Brand Conversion, spec 2026-10-05-brand-conversion §2.3/§3) — tỉ lệ
// chuyển đổi dạng tiếng Việt, che khoá bí mật, dựng đoạn script hiển thị. Tách khỏi component để test
// không phải dựng React.

// Khoá bí mật 40 ký tự base62 (BrandDealConst.SecretKeyLength, payment) — chỉ trả đầy đủ lúc sinh/tạo
// lại, các lần sau chỉ có 4 ký tự cuối; che phần còn lại bằng dấu chấm tròn.
const SECRET_KEY_LENGTH = 40;

/** Tỉ lệ chuyển đổi = conversions/clicks, hiện dạng phần trăm 1 chữ số thập phân, dấu phẩy Việt Nam
 * (vd 3/94 ⇒ "3,2%"). 0 lượt bấm ⇒ "0%" (không chia cho 0). */
export const formatRate = (conversions: number, clicks: number): string => {
  if (!clicks) return '0%';
  const rate = (conversions / clicks) * 100;
  return `${rate.toFixed(1).replace('.', ',')}%`;
};

/** Che khoá bí mật chỉ còn 4 ký tự cuối — dùng mọi lần gọi SAU lần sinh/tạo lại đầu tiên (BE chỉ trả
 * `secretLast4` từ lúc đó, không còn khoá đầy đủ để hiện). */
export const maskSecret = (last4: string): string =>
  '•'.repeat(Math.max(0, SECRET_KEY_LENGTH - last4.length)) + last4;

/** Dựng đoạn script hiển thị ở mục "Cài mã đo chuyển đổi" — CÙNG nội dung BE trả về
 * (`BrandConversionSetup.BuildScriptSnippet`, payment), dùng cho hiển thị/test khi chưa có response
 * thật. BE là nguồn sự thật cho đoạn hiển thị thật (rule: hiện nguyên văn `scriptSnippet` từ API). */
export const buildScriptSnippet = (publicKey: string, scriptUrl: string): string =>
  '<script>window.cabConversion=window.cabConversion||function(){(window.cabq=window.cabq||[]).push(arguments)};</script>\n' +
  `<script src="${scriptUrl}" data-key="${publicKey}" async></script>`;

// ─────────── Final-review I2: biểu đồ theo ngày — nhãn dd/MM, lấp ngày trống ───────────

export interface BrandDayStat {
  /** "yyyy-MM-dd" hoặc "yyyy-MM-ddTHH:mm:ss" (BE trả `DateTime`, FE luôn chỉ lấy 10 ký tự đầu). */
  date: string;
  clicks: number;
  conversions: number;
}

/** Nhãn ngày hiển thị trên biểu đồ — "dd/MM", KHÔNG còn phần giờ (BE trả `DateTime` nên JSON hoá thành
 * "2026-10-05T00:00:00" — cắt đúng 10 ký tự đầu trước khi tách, không phụ thuộc có phần giờ hay không). */
export const formatDayLabel = (isoDate: string): string => {
  const [, month, day] = isoDate.slice(0, 10).split('-');
  return `${day}/${month}`;
};

/** Lấp các ngày không có dữ liệu bằng 0 lượt bấm/chuyển đổi trong khoảng [from, to] (dạng "yyyy-MM-dd");
 * bỏ trống from/to ⇒ lấy khoảng ngày nhỏ nhất..lớn nhất đang có trong `byDay`. Không lấp được (không có
 * from/to lẫn không có dữ liệu, hoặc from > to) ⇒ trả mảng rỗng. Mục đích: 2 ngày có dữ liệu cách xa
 * nhau không bị nối thẳng thành 1 đường chéo sai lệch xu hướng trên biểu đồ Line. */
export const fillDays = (byDay: BrandDayStat[], from?: string | null, to?: string | null): BrandDayStat[] => {
  const byDate = new Map<string, BrandDayStat>();
  byDay.forEach((d) => byDate.set(d.date.slice(0, 10), d));

  let startKey = from ? from.slice(0, 10) : undefined;
  let endKey = to ? to.slice(0, 10) : undefined;
  if (!startKey || !endKey) {
    const knownKeys = Array.from(byDate.keys()).sort();
    if (knownKeys.length === 0) return [];
    startKey = startKey || knownKeys[0];
    endKey = endKey || knownKeys[knownKeys.length - 1];
  }

  const start = new Date(`${startKey}T00:00:00Z`);
  const end = new Date(`${endKey}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start.getTime() > end.getTime()) return [];

  const DAY_MS = 24 * 60 * 60 * 1000;
  const result: BrandDayStat[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += DAY_MS) {
    const key = new Date(t).toISOString().slice(0, 10);
    const existing = byDate.get(key);
    result.push({ date: key, clicks: existing?.clicks ?? 0, conversions: existing?.conversions ?? 0 });
  }
  return result;
};
